import React, { useState } from 'react';
import { X, Play, Database } from 'lucide-react';
import { ProjectMetadata } from '../types';
import { createDefaultProjectMetadata } from '../utils/validation';

interface ProjectContextModalProps {
    isOpen: boolean;
    onClose: () => void;
    onStart: (context: ProjectContext) => void;
}

export interface ProjectContext {
    projectName: string;
    description: string;
    requirements: string[];
    metadata: ProjectMetadata; // Enhanced with full metadata
}

export const ProjectContextModal: React.FC<ProjectContextModalProps> = ({ isOpen, onClose, onStart }) => {
    const [name, setName] = useState('New Project');
    const [desc, setDesc] = useState('');
    const [reqs, setReqs] = useState('');
    const [standard, setStandard] = useState<'AIAG_V4' | 'AIAG_VDA' | 'SAE_J1739' | 'ISO_14971' | 'MIL_STD_1629A' | 'CUSTOMER_CUSTOM'>('AIAG_VDA');

    if (!isOpen) return null;

    const handleStart = () => {
        const requirementsList = reqs.split('\n').filter(r => r.trim().length > 0);
        if (requirementsList.length === 0) {
            alert("Please enter at least one requirement.");
            return;
        }

        // Create proper metadata following standards
        const metadata = createDefaultProjectMetadata(name, 'user');
        metadata.description = desc;
        metadata.projectType = 'Prototype'; // Default to Prototype for new projects
        metadata.developmentPhase = 'Concept';
        metadata.fmeaStandard = standard;

        onStart({
            projectName: name,
            description: desc,
            requirements: requirementsList,
            metadata
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-card w-full max-w-2xl rounded-xl shadow-2xl border border-border flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-border flex justify-between items-center bg-muted/30">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Database className="text-primary" size={24} />
                            Start New Simulation
                        </h2>
                        <p className="text-text-secondary text-sm mt-1">Define the context for this Magnus Genesis run.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-full">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6 overflow-y-auto">

                    {/* Project Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-primary">Project / Product Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Industrial Paint Mixer"
                            className="w-full p-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-primary">System Description (Context)</label>
                        <textarea
                            value={desc}
                            onChange={e => setDesc(e.target.value)}
                            placeholder="Describe the system, its environment, and intended use..."
                            rows={3}
                            className="w-full p-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                        />
                    </div>

                    {/* Requirements */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-primary">
                            Customer Requirements
                            <span className="text-text-secondary font-normal ml-2">(One per line)</span>
                        </label>
                        <textarea
                            value={reqs}
                            onChange={e => setReqs(e.target.value)}
                            placeholder="CR-001: Must operate at 500 RPM&#10;CR-002: Noise level < 60dB&#10;CR-003: Waterproof housing (IP67)"
                            rows={8}
                            className="w-full p-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all font-mono text-sm"
                        />
                    </div>

                    {/* FMEA Standard */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-text-primary">FMEA Standard / Compliance Form</label>
                        <select
                            value={standard}
                            onChange={e => setStandard(e.target.value as any)}
                            className="w-full p-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all appearance-none cursor-pointer"
                        >
                            <option value="AIAG_VDA">🚗 AIAG-VDA 2019 (Modern - AP Based)</option>
                            <option value="AIAG_V4">🚜 AIAG 4th Edition (Legacy - RPN Based)</option>
                            <option value="SAE_J1739">🛩️ SAE J1739 (Aerospace/Auto)</option>
                            <option value="ISO_14971">🩺 ISO 14971 (Medical Devices)</option>
                            <option value="MIL_STD_1629A">🛡️ MIL-STD-1629A (Defense)</option>
                            <option value="CUSTOMER_CUSTOM">📄 Customer Specific Form</option>
                        </select>
                        <p className="text-[11px] text-text-secondary">This dictates the FMEA table headers, risk calculation logic, and AI coaching rules.</p>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-6 border-t border-border bg-muted/30 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-text-secondary hover:text-text-primary hover:bg-muted rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleStart}
                        className="px-6 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
                    >
                        <Play size={18} fill="currentColor" />
                        Initialize Agents
                    </button>
                </div>

            </div>
        </div>
    );
};
